import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';
import {
  FaArrowLeft,
  FaGraduationCap,
  FaChartLine,
  FaQuestionCircle,
  FaTrophy,
  FaUsers,
  FaChevronDown,
  FaFlask,
  FaBook,
  FaClipboardCheck,
  FaUserShield,
  FaCog,
  FaComments
} from 'react-icons/fa';
import soundEffects from '../utils/soundEffects';
import './Help.css';

const SECTIONS_STUDENT = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <FaGraduationCap />,
    iconColor: 'blue',
    steps: [
      { text: 'After logging in you land on your <strong>Dashboard</strong>. This is your home base — you can see your level, XP, streak, and daily fact.' },
      { text: 'Tap <strong>Got it!</strong> on the daily fact card to earn XP and keep your streak alive. You get one new fact per day.' },
      { text: 'Use the <strong>bottom navigation bar</strong> to jump between Home, Learn, Ask, Class, and Simulate.' },
    ],
    tip: 'Try to log in every day so your streak keeps growing — longer streaks unlock special badges!',
  },
  {
    id: 'learning',
    title: 'Courses & Lessons',
    icon: <FaBook />,
    iconColor: 'green',
    steps: [
      { text: 'Tap <strong>Learn</strong> in the nav bar to see all available courses. Each course card shows your progress.' },
      { text: 'Open a course to see its <strong>units</strong>. Each unit contains multiple lessons and may end with a quiz or unit test.' },
      { text: 'Complete lessons in order — locked lessons unlock once you finish the one before them.' },
      { text: 'After finishing a lesson you earn <strong>XP</strong>. Quizzes award bonus XP based on your score.' },
    ],
    tip: 'If your teacher has assigned specific lessons, check the Class tab — assignments appear there with due dates.',
  },
  {
    id: 'ask',
    title: 'Ask (AI Assistant)',
    icon: <FaQuestionCircle />,
    iconColor: 'purple',
    steps: [
      { text: 'Tap <strong>Ask</strong> in the nav bar to open the AI assistant.' },
      { text: 'Type any finance or investing question and get an instant, student-friendly answer.' },
      { text: 'The assistant remembers your conversation history during the session, so you can ask follow-ups.' },
    ],
  },
  {
    id: 'simulations',
    title: 'Portfolio Simulator',
    icon: <FaFlask />,
    iconColor: 'cyan',
    steps: [
      { text: 'Tap <strong>Simulate</strong> to access the portfolio simulator. You start with virtual cash and can buy/sell real stocks.' },
      { text: 'Search for stocks by name or ticker, review the price chart, then place a <strong>Buy</strong> or <strong>Sell</strong> order.' },
      { text: 'Your portfolio value updates with real market prices. Try to grow your balance!' },
      { text: 'You can also add stocks to your <strong>Watchlist</strong> to track them without buying.' },
    ],
    tip: 'The leaderboard ranks everyone by portfolio value, investing earnings, and quiz earnings — compete with classmates!',
  },
  {
    id: 'class',
    title: 'My Class & Assignments',
    icon: <FaUsers />,
    iconColor: 'blue',
    steps: [
      { text: 'If your teacher added you to a class, the <strong>Class</strong> tab shows your assigned lessons and quizzes.' },
      { text: 'Tap an assignment to jump straight to the lesson or quiz. A green check means you have completed it.' },
      { text: 'Your teacher can see your progress, so keep up with assignments!' },
    ],
  },
  {
    id: 'badges',
    title: 'Badges & Leaderboard',
    icon: <FaTrophy />,
    iconColor: 'amber',
    steps: [
      { text: 'You earn <strong>badges</strong> automatically as you hit milestones — first lesson, streak of 7, etc.' },
      { text: 'View all your badges from the Dashboard, and see which ones are still locked.' },
      { text: 'The <strong>Leaderboard</strong> (accessible from Dashboard) shows rankings by portfolio value, total earnings, or quiz earnings.' },
    ],
  },
];

const SECTIONS_TEACHER = [
  {
    id: 'teacher-overview',
    title: 'Teacher Overview',
    icon: <FaUserShield />,
    iconColor: 'green',
    steps: [
      { text: 'As a teacher you have access to <strong>League Management</strong> (the group icon in the top bar or Dashboard).' },
      { text: 'A "league" is your classroom. Students join using a class code, or you can create student accounts directly.' },
      { text: 'You can manage multiple leagues if you teach several classes.' },
    ],
  },
  {
    id: 'manage-students',
    title: 'Managing Students',
    icon: <FaUsers />,
    iconColor: 'blue',
    steps: [
      { text: 'Open a league and tap <strong>View Students</strong> to see everyone in the class.' },
      { text: 'Use <strong>Create Student Account</strong> to make accounts for students who haven\'t signed up yet. You set their username and password.' },
      { text: 'Tap the <strong>Analytics</strong> button on any student row to view their individual progress, quiz scores, and activity timeline.' },
    ],
    tip: 'Student analytics show the last 30 days of activity — great for checking engagement before parent conferences.',
  },
  {
    id: 'assignments',
    title: 'Assigning Lessons & Quizzes',
    icon: <FaClipboardCheck />,
    iconColor: 'purple',
    steps: [
      { text: 'From a league, tap <strong>Assignments</strong> to manage what the class should work on.' },
      { text: 'Tap <strong>Assign Lesson</strong> or <strong>Assign Quiz</strong>, then pick from the available content.' },
      { text: 'You can also <strong>Bulk Assign</strong> an entire course or unit at once.' },
      { text: 'Students see assigned items in their Class tab and can start them directly.' },
    ],
  },
  {
    id: 'curriculum',
    title: 'Custom Curriculum',
    icon: <FaBook />,
    iconColor: 'amber',
    steps: [
      { text: 'Tap <strong>Curriculum</strong> in the league view to create your own courses, units, and lessons.' },
      { text: 'You can write lessons with the <strong>rich text editor</strong> or upload a <strong>PDF</strong> and have it converted automatically.' },
      { text: 'Add quizzes to your custom lessons, then assign them to students just like built-in content.' },
    ],
    tip: 'Custom curriculum is only visible within your league, so you can tailor content to your class.',
  },
  {
    id: 'analytics',
    title: 'Class Analytics & Graphs',
    icon: <FaChartLine />,
    iconColor: 'rose',
    steps: [
      { text: 'Tap <strong>Analytics</strong> on the league card to see class-wide stats: activity trends, quiz score distribution, level distribution, and student rankings.' },
      { text: 'The <strong>Daily Activity</strong> line chart shows lessons and quizzes completed per day over the last 30 days.' },
      { text: 'The <strong>Student Rankings</strong> table lets you quickly spot top performers and students who may need help.' },
      { text: 'Click any student\'s <strong>Analytics</strong> button for a deep-dive into their individual engagement and portfolio.' },
    ],
  },
  {
    id: 'challenges',
    title: 'League Challenges',
    icon: <FaTrophy />,
    iconColor: 'amber',
    steps: [
      { text: 'From a league, tap <strong>Challenges</strong> to create timed competitions for your students.' },
      { text: 'Set a challenge type (quiz, portfolio, etc.), deadline, and reward.' },
      { text: 'Students access challenges from the Dashboard or Challenges page.' },
    ],
  },
];

const SECTIONS_NORMAL = [
  {
    id: 'normal-start',
    title: 'Getting Started',
    icon: <FaGraduationCap />,
    iconColor: 'blue',
    steps: [
      { text: 'Welcome! After signing up you start at <strong>Level 1</strong> with 0 XP. Complete daily facts and lessons to level up.' },
      { text: 'Your <strong>Dashboard</strong> shows your streak, level, XP bar, and a daily financial fact.' },
      { text: 'Use the bottom nav to move between Home, Learn, Ask, Forum, and Simulate.' },
    ],
    tip: 'The Guest mode lets you explore without an account, but progress won\'t be saved. Sign up to keep your streaks and badges!',
  },
  {
    id: 'learning-normal',
    title: 'Courses & Lessons',
    icon: <FaBook />,
    iconColor: 'green',
    steps: [
      { text: 'Tap <strong>Learn</strong> to browse all courses. Each one covers a financial topic (budgeting, investing, crypto, etc.).' },
      { text: 'Lessons are unlocked sequentially. Complete one to unlock the next.' },
      { text: 'Quizzes at the end of units test what you learned and award bonus XP.' },
    ],
  },
  {
    id: 'portfolio-normal',
    title: 'Portfolio Simulator',
    icon: <FaFlask />,
    iconColor: 'cyan',
    steps: [
      { text: 'The simulator gives you virtual cash to invest in real stocks with live prices.' },
      { text: 'Build a portfolio, track your gains/losses, and compete on the <strong>Leaderboard</strong>.' },
      { text: 'Add stocks to your <strong>Watchlist</strong> to monitor prices before you buy.' },
    ],
  },
  {
    id: 'forum-normal',
    title: 'Forum',
    icon: <FaComments />,
    iconColor: 'purple',
    steps: [
      { text: 'The <strong>Forum</strong> is where you can ask questions, share tips, and discuss financial topics with other users.' },
      { text: 'Create a post or reply to existing threads. Be respectful and stay on topic!' },
    ],
  },
  {
    id: 'ask-normal',
    title: 'AI Assistant',
    icon: <FaQuestionCircle />,
    iconColor: 'purple',
    steps: [
      { text: 'Tap <strong>Ask</strong> to chat with the AI about any financial question.' },
      { text: 'The AI gives student-friendly explanations and can help you understand tricky concepts from lessons.' },
    ],
  },
  {
    id: 'profile-normal',
    title: 'Profile & Settings',
    icon: <FaCog />,
    iconColor: 'amber',
    steps: [
      { text: 'Tap the profile icon in the top bar to view and edit your profile.' },
      { text: 'You can change your username, upload a profile picture, and view your all-time stats.' },
    ],
  },
];

const Help = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [openSections, setOpenSections] = useState({});

  const isTeacher = state.user?.isTeacher || state.user?.isAdmin || ['1','2','3','master_admin'].includes(state.user?.adminLevel);
  const isStudent = !!state.user?.leagueId && !isTeacher;

  let role = 'normal';
  let roleLabel = 'Standard Account';
  let sections = SECTIONS_NORMAL;

  if (isTeacher) {
    role = 'teacher';
    roleLabel = 'Teacher / Admin';
    sections = SECTIONS_TEACHER;
  } else if (isStudent) {
    role = 'student';
    roleLabel = 'Student';
    sections = SECTIONS_STUDENT;
  }

  const toggle = (id) => {
    soundEffects.playClick();
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="help-container">
      <div className="help-top-bar">
        <div className="help-top-bar-content">
          <button className="help-back-btn" onClick={() => { soundEffects.playClick(); navigate(-1); }}>
            <FaArrowLeft />
          </button>
          <h1 className="help-top-bar-title">Help Center</h1>
        </div>
      </div>

      <div className="help-main">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="help-hero"
        >
          <h2 className="help-hero-title">How can we help?</h2>
          <p className="help-hero-sub">Step-by-step guides tailored to your account</p>
          <span className={`help-role-tag ${role}`}>{roleLabel}</span>
        </motion.div>

        <div className="help-sections">
          {sections.map((section, idx) => {
            const isOpen = !!openSections[section.id];
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="help-section"
              >
                <button className="help-section-header" onClick={() => toggle(section.id)}>
                  <span className={`help-section-icon ${section.iconColor}`}>{section.icon}</span>
                  <h3 className="help-section-title">{section.title}</h3>
                  <FaChevronDown className={`help-section-chevron ${isOpen ? 'open' : ''}`} />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="help-section-body">
                        {section.steps.map((step, i) => (
                          <div key={i} className="help-step">
                            <span className="help-step-num">{i + 1}</span>
                            <span className="help-step-text" dangerouslySetInnerHTML={{ __html: step.text }} />
                          </div>
                        ))}
                        {section.tip && (
                          <div className="help-tip">
                            <span className="help-tip-icon">💡</span>
                            <span className="help-tip-text">{section.tip}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Help;
